import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BarChartDate from "../../chart/BarChartDate";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconView } from "../../assets/icon/metric/IconView";
import { IconLike } from "../../assets/icon/metric/IconLike";
import { IconReply } from "../../assets/icon/metric/IconReply";
import { IconRepost } from "../../assets/icon/metric/IconRepost";
import { IconUsers } from "../../assets/icon/metric/IconUsers";
import { IconFollower } from "../../assets/icon/metric/IconFollower";
import { IconThreads } from "../../assets/icon/metric/IconThreads";
import numeral from "numeral";
import styled from "styled-components";
import Confetti from "react-confetti";
import { motion } from "framer-motion";

// 스타일드 컴포넌트로 애니메이션 효과 추가
const AnimatedStack = styled(Stack)`
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: ${(props) => (props.loading ? 0.8 : 1)};
  transform: scale(${(props) => (props.loading ? 0.98 : 1)});
  position: relative;
  overflow: hidden;
`;

const ChartContainer = styled(Stack)`
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: ${(props) => (props.loading ? 0.7 : 1)};
`;

// 숫자 강조 애니메이션을 위한 Wrapper 정의
const NumberWrapper = styled(motion.div)`
  display: inline-block; // 숫자가 인라인 블록 요소처럼 동작하도록 설정
`;

const StatusTitle = ({ type, value, isFullWidth, isMobile }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ width: isFullWidth ? "100%" : "auto" }}
    >
      <Box
        sx={{
          padding: theme.spacing(0.25),
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.background.paper,
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          height: "fit-content",
        }}
      >
        {type === "profileVisit" && <IconUsers size={16} />}
        {type === "view" && <IconView size={16} />}
        {type === "like" && <IconLike size={16} />}
        {type === "reply" && <IconReply size={16} />}
        {type === "repost" && <IconRepost size={16} />}
        {type === "threads" && <IconThreads size={16} />}
        {type === "follower" && <IconFollower size={16} />}
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {type === "profileVisit" && t("common.profileView")}
        {type === "view" && t("common.views")}
        {type === "like" && t("common.likes")}
        {type === "reply" && t("common.replies")}
        {type === "repost" && t("common.reposts")}
        {type === "threads" && t("common.threads")}
        {type === "follower" && t("common.followers")}
      </Typography>
    </Stack>
  );
};

export const CardStatus = ({
  type,
  value,
  monthlyViews,
  postLoading,
  isMobile,
  isLoading = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [titleWidth, setTitleWidth] = useState(0);
  const [prevMonthlyViews, setPrevMonthlyViews] = useState([]);
  const [displayValue, setDisplayValue] = useState(value);
  const [prevValueForConfetti, setPrevValueForConfetti] = useState(value);
  const [showConfetti, setShowConfetti] = useState(false);
  const [runConfetti, setRunConfetti] = useState(false);
  const [animateNumber, setAnimateNumber] = useState(false);
  const numberRef = useRef(null);

  useEffect(() => {
    let timerId;
    if (value !== undefined && !postLoading) {
      if (prevValueForConfetti !== value) {
        setShowConfetti(true);
        timerId = setTimeout(() => setShowConfetti(false), 4000);
        setPrevValueForConfetti(value);
      }
      setDisplayValue(value);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [value, postLoading]);

  useEffect(() => {
    setPrevValueForConfetti(value);
  }, []);

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
      if (titleRef.current) {
        setTitleWidth(titleRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateContainerSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateContainerSize();
    }

    if (titleRef.current) {
      resizeObserver.observe(titleRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, titleRef]);

  useEffect(() => {
    if (monthlyViews && monthlyViews.length > 0 && !postLoading) {
      setPrevMonthlyViews(monthlyViews);
    }
  }, [monthlyViews, postLoading]);

  const chartData =
    postLoading && prevMonthlyViews.length > 0
      ? prevMonthlyViews
      : monthlyViews;

  const formattedValue = numeral(displayValue).format(
    displayValue < 1000 || displayValue % 1000 < 100 ? "0a" : "0.0a"
  );

  // 차트 너비 계산: 안전한 기본값 사용
  const chartWidth = containerWidth > 0 ? Math.max(200, containerWidth * 0.6) : 300;

  // 콘페티 폭발 지점 계산
  const confettiX = numberRef.current
    ? numberRef.current.offsetLeft + numberRef.current.offsetWidth / 2
    : containerWidth / 2;
  const confettiY = numberRef.current
    ? numberRef.current.offsetTop + numberRef.current.offsetHeight / 2
    : containerHeight / 2;

  // 디자인에 맞는 콘페티 색상 팔레트 정의
  const confettiColors = [
    "#FFFFFF", // White
    theme.palette.background.paper, // Light Background
    theme.palette.text.secondary, // Light Gray
    theme.palette.text.secondary, // Medium Gray
    "#FFD700", // Gold (accent color used in number animation)
    "#FFEBCD", // Blanched Almond (lighter gold/beige tone)
  ];

  // 스켈레톤 로딩 상태 렌더링
  if (isLoading) {
    return (
      <Stack
        direction="column"
        spacing={isMobile ? 2 : 6}
        sx={{ width: "100%" }}
      >
        {/* 타이틀 스켈레톤 */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Skeleton variant="rectangular" width={24} height={24} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="text" width={80} height={20} />
        </Stack>
        
        {/* 값 스켈레톤 */}
        <Skeleton variant="text" width={60} height={isMobile ? 32 : 40} />
        
        {/* 차트 스켈레톤 (profileVisit 타입인 경우) */}
        {type === "profileVisit" && (
          <Skeleton 
            variant="rectangular" 
            width="60%" 
            height={160} 
            sx={{ 
              position: "absolute",
              right: isMobile ? -24 : 0,
              bottom: isMobile ? -48 : -24,
            }} 
          />
        )}
      </Stack>
    );
  }

  return (
    <AnimatedStack
      ref={containerRef}
      direction="column"
      spacing={isMobile ? 2 : 6}
      sx={{ width: "100%" }}
      loading={postLoading ? 1 : 0}
    >
      {/* Confetti with updated colors */}
      {runConfetti && (
        <Confetti
          width={containerWidth}
          height={containerHeight}
          run={runConfetti}
          recycle={false}
          numberOfPieces={350} // 조각 수를 조금 줄여서 덜 산만하게 조정
          gravity={0.15} // 중력을 약간 약하게
          initialVelocityX={{ min: -7, max: 7 }} // 수평 속도 범위 축소
          initialVelocityY={{ min: 10, max: 20 }} // 수직 속도 약간 낮춤
          confettiSource={{
            x: confettiX,
            y: confettiY,
            w: numberRef.current ? numberRef.current.offsetWidth : 50,
            h: numberRef.current ? numberRef.current.offsetHeight : 30,
          }}
          colors={confettiColors} // 수정된 색상 팔레트 적용
          style={{ position: "absolute", top: 0, left: 0, zIndex: 10 }}
          onConfettiComplete={() => setRunConfetti(false)}
        />
      )}

      <Box ref={titleRef}>
        <StatusTitle type={type} />
      </Box>
      <Box>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            fontWeight: 700,
            fontFamily: '"SUIT Variable", sans-serif',
          }}
        >
          <NumberWrapper
            ref={numberRef}
            animate={
              animateNumber
                ? {
                    scale: [1, 1.3, 1],
                    color: ["inherit", "#FFD700", "inherit"],
                  }
                : {}
            }
            transition={{ duration: 0.8, ease: [0.17, 0.67, 0.83, 0.67] }}
          >
            {formattedValue !== undefined ? formattedValue : "-"}
          </NumberWrapper>
        </Typography>
      </Box>
      {type === "profileVisit" && (
        <ChartContainer
          alignItems="flex-end"
          justifyContent="flex-end"
          sx={{
            width: `${chartWidth}px`,
            position: "absolute",
            right: isMobile ? -24 : 0,
            bottom: isMobile ? -48 : -24, // 하단 여백 증가로 tick 잘림 방지
            zIndex: 1,
          }}
          loading={postLoading ? 1 : 0}
        >
          {chartData && chartData.length > 0 && (
            <BarChartDate
              width={chartWidth}
              height={isMobile ? 160 : 160} // 높이 증가로 tick 영역 확보
              xTicks={4}
              yTicks={2}
              data={chartData}
              fillColor={theme.palette.text.primary}
              axisColor={theme.palette.text.secondary}
              tickColor={theme.palette.text.secondary}
              isMobile={isMobile}
            />
          )}
        </ChartContainer>
      )}
    </AnimatedStack>
  );
};
